import React, { useState, useMemo, useEffect, useRef, JSX } from 'react';
import { mentors } from '../data/mentors';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../components/SEO';
import { formatExpertise } from '../utils/mentorUtils';
import { languageToFlag } from '../utils/languageUtils';
import { useMentorModal } from '../hooks/useMentorModal';
import { createPersonLd } from '../utils/schema';
import { useMentors } from '../hooks/useMentors';
import { getMentorImageUrl } from '../utils/imageUtils';
import MentorCardSkeleton from '../components/MentorCardSkeleton';

// Define allowed expertise categories
const ALLOWED_EXPERTISE = [
  'Business & Strategy',
  'Game Design & Creative',
  'Technology & Development',
  'Marketing & Community',
  'Production & Management',
  'Legal & IP',
];

// Define allowed stages
const ALLOWED_STAGES = ['Startup', 'Growth', 'Mature'];

// Define allowed languages
const ALLOWED_LANGUAGES = ['English', 'Dutch', 'French'];

// Add this color mapping at the top of the file
const EXPERTISE_COLORS: Record<string, { bg: string; text: string }> = {
  'Business & Strategy': { bg: 'bg-blue-100', text: 'text-blue-800' },
  'Game Design & Creative': { bg: 'bg-purple-100', text: 'text-purple-800' },
  'Technology & Development': { bg: 'bg-cyan-100', text: 'text-cyan-800' },
  'Marketing & Community': { bg: 'bg-green-100', text: 'text-green-800' },
  'Production & Management': { bg: 'bg-orange-100', text: 'text-orange-800' },
  'Legal & IP': { bg: 'bg-red-100', text: 'text-red-800' },
};

// Pattern generation function
const generatePatternForMentor = (mentorExpertise: string[] = []) => {
  return (
    <div className="absolute inset-0 z-40 flex flex-wrap items-center justify-end overflow-visible pl-32 pr-4 xl:justify-center xl:pl-48 xl:pr-0">
      {mentorExpertise.map((exp, index) => {
        const randomRotation = Math.random() > 0.5 ? 'rotate-1' : '-rotate-1';
        const randomSize = Math.random() > 0.7 ? 'text-xs' : 'text-xs';

        return (
          <span
            key={`${exp}-${index}`}
            className={`mr-2 whitespace-nowrap rounded-full px-2 py-0.5 font-medium ${randomSize} ${randomRotation} ${EXPERTISE_COLORS[exp]?.bg || 'bg-gray-500/70'} ${EXPERTISE_COLORS[exp]?.text || 'text-white'} `}
          >
            {exp}
          </span>
        );
      })}
    </div>
  );
};

// Add this helper function at the top level
const getBaseColor = (expertise: string) => {
  const colorClass = EXPERTISE_COLORS[expertise]?.bg || '';
  return colorClass.split('-')[1]; // Get 'orange' from 'bg-orange-500/70'
};

const MentorsOverviewPage: React.FC = () => {
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [filters, setFilters] = useState({
    expertise: ['All'] as string[],
    stages: [] as string[],
    languages: [] as string[],
    price: [] as string[],
  });

  const { openMentorModal } = useMentorModal();

  // Fetch mentors from API or use fallback
  const { mentors: apiMentors, isLoading, error, isUsingFallback } = useMentors();

  // Use API mentors if available, otherwise fall back to static mentors
  const mentorsList = apiMentors.length > 0 ? apiMentors : mentors;

  // Get mentorId from query parameters - handle both cases
  const searchParams = new URLSearchParams(location.search);
  const mentorId = searchParams.get('mentorId') || searchParams.get('mentorid');

  // Find the mentor if mentorId is present
  const selectedMentor = useMemo(() => {
    if (mentorId) {
      return mentorsList.find((m) => m.id === mentorId);
    }
    return null;
  }, [mentorId, mentorsList]);

  // Filter the expertise options to only show allowed categories
  const filterOptions = useMemo(
    () => ({
      expertise: [...new Set(mentorsList.flatMap((m) => m.expertise || []))]
        .filter((exp) => ALLOWED_EXPERTISE.includes(exp))
        .sort(),
      stages: ALLOWED_STAGES,
      languages: ALLOWED_LANGUAGES,
      price: [...new Set(mentorsList.flatMap((m) => m.price || []))].sort(),
    }),
    [mentorsList]
  );

  // Modified filter logic with corrected type check
  const filteredMentors = useMemo(() => {
    return mentorsList.filter((mentor) => {
      const matchesSearch =
        !searchTerm ||
        mentor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mentor.expertise?.some((exp) => exp.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesExpertise =
        filters.expertise.includes('All') ||
        mentor.expertise?.some((exp) => filters.expertise.includes(exp));

      const matchesStages =
        filters.stages.length === 0 ||
        (mentor.stages && mentor.stages.length === 0) ||
        mentor.stages?.some((stage) => filters.stages.includes(stage));

      const matchesLanguages =
        filters.languages.length === 0 ||
        mentor.languages?.some((lang) => filters.languages.includes(lang));

      const matchesPrice =
        filters.price.length === 0 || mentor.price?.some((price) => filters.price.includes(price));

      return matchesSearch && matchesExpertise && matchesStages && matchesLanguages && matchesPrice;
    });
  }, [searchTerm, filters, mentorsList]);

  const handleFilterChange = (category: keyof typeof filters, value: string) => {
    setFilters((prev) => {
      if (category === 'expertise') {
        if (value === 'All') {
          return { ...prev, expertise: ['All'] };
        }
        const newExpertise = prev.expertise.includes('All')
          ? [value]
          : prev.expertise.includes(value)
            ? prev.expertise.filter((item) => item !== value)
            : [...prev.expertise, value];
        return { ...prev, expertise: newExpertise.length === 0 ? ['All'] : newExpertise };
      }
      return {
        ...prev,
        [category]: prev[category].includes(value)
          ? prev[category].filter((item) => item !== value)
          : [...prev[category], value],
      };
    });
  };

  // Remove individual dropdown states and use single activeDropdown state
  const toggleDropdown = (dropdownName: string) => {
    setActiveDropdown(activeDropdown === dropdownName ? null : dropdownName);
  };

  // Update helper to check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      !filters.expertise.includes('All') ||
      filters.expertise.length > 1 || // Check if anything other than just 'All' is selected
      filters.stages.length > 0 || // Any stages selected
      filters.languages.length > 0 || // Any languages selected
      filters.price.length > 0 // Any price options selected
    );
  }, [filters]);

  // Add clear all filters function
  const clearAllFilters = () => {
    setFilters({
      expertise: ['All'],
      stages: [],
      languages: [],
      price: [],
    });
  };

  // Add click outside handler
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Use the memoized patterns with null check for expertise
  const mentorPatterns = useMemo(() => {
    return mentorsList.reduce(
      (patterns, mentor) => {
        patterns[mentor.id] = generatePatternForMentor(mentor.expertise || []);
        return patterns;
      },
      {} as Record<string, JSX.Element>
    );
  }, [mentorsList]);

  // Create schema for the mentors list
  const schema = useMemo(() => {
    return createPersonLd(
      'Game Industry Mentors',
      'Connect with experienced mentors in the game industry',
      '/assets/logo.webp',
      'Find your perfect gaming industry mentor',
      'Game Industry Mentors'
    );
  }, []);

  // Create schema for the selected mentor if one is selected
  const mentorSchema = useMemo(() => {
    if (selectedMentor) {
      return createPersonLd(
        selectedMentor.name,
        `https://gamementors.be/mentors/${selectedMentor.id}`,
        selectedMentor.image,
        selectedMentor.description,
        selectedMentor.expertise?.[0],
        [selectedMentor.linkedIn, selectedMentor.twitter, selectedMentor.website].filter(
          (url): url is string => !!url
        )
      );
    }
    return undefined;
  }, [selectedMentor]);

  // Note: URL handling for mentor modals is now managed by MentorModalContext

  const handleMentorClick = (mentorId: string) => {
    const mentor = mentorsList.find((m) => m.id === mentorId);
    if (mentor) {
      openMentorModal(mentor);
    }
  };

  return (
    <>
      <SEO
        title={
          selectedMentor
            ? `${selectedMentor.name} - Game Industry Mentor | GameMentors`
            : 'Find Your Perfect Gaming Industry Mentor | GameMentors'
        }
        description={
          selectedMentor
            ? selectedMentor.description
            : 'Connect with experienced mentors in the game industry. Get guidance on business strategy, game design, technology, marketing, and more.'
        }
        ogTitle={
          selectedMentor
            ? `Connect with ${selectedMentor.name} - Game Industry Mentor`
            : 'Find Your Perfect Gaming Industry Mentor'
        }
        ogDescription={
          selectedMentor
            ? selectedMentor.description
            : 'Connect with experienced mentors in the game industry. Get guidance on business strategy, game design, technology, marketing, and more.'
        }
        ogImage={selectedMentor?.image || '/assets/logo.webp'}
        ogImageWidth={800}
        ogImageHeight={800}
        ogImageAlt={
          selectedMentor ? `${selectedMentor.name} - Game Industry Mentor` : 'GameMentors Logo'
        }
        twitterTitle={
          selectedMentor
            ? `${selectedMentor.name} - Game Industry Mentor`
            : 'Find Your Perfect Gaming Industry Mentor'
        }
        twitterDescription={
          selectedMentor
            ? selectedMentor.description
            : 'Connect with experienced mentors in the game industry. Get guidance on business strategy, game design, technology, marketing, and more.'
        }
        twitterImage={selectedMentor?.image || '/assets/logo.webp'}
        twitterImageAlt={
          selectedMentor ? `${selectedMentor.name} - Game Industry Mentor` : 'GameMentors Logo'
        }
        linkedinTitle={
          selectedMentor
            ? `${selectedMentor.name} - Game Industry Mentor on GameMentors`
            : 'Find Your Perfect Gaming Industry Mentor on GameMentors'
        }
        linkedinDescription={
          selectedMentor
            ? selectedMentor.description
            : 'Connect with experienced mentors in the game industry. Get guidance on business strategy, game design, technology, marketing, and more.'
        }
        type={selectedMentor ? 'profile' : 'website'}
        schema={selectedMentor ? mentorSchema : schema}
      />

      <Helmet>
        <title>All Mentors - Find Your Perfect Mentor</title>
        <meta name="description" content="Browse our complete list of experienced mentors" />
      </Helmet>

      <main className="min-h-screen bg-gray-50 pt-16 dark:bg-gray-900">
        <div className="mx-auto max-w-screen-xl px-4 py-8 lg:px-6 lg:py-16">
          {/* Hero Section */}
          <section className="relative overflow-hidden">
            {/* Content with split layout */}
            <div className="relative mx-auto max-w-screen-xl">
              <div className="flex flex-col gap-8 lg:flex-row">
                {/* Main Header Section - 2/3 width on desktop */}
                <div className="lg:w-2/3">
                  <h1 className="mb-8 text-xl font-medium tracking-tight text-gray-900 sm:mb-12 sm:text-3xl md:text-4xl">
                    Find the perfect mentor for your game studio
                  </h1>
                </div>

                {/* Chat and Store Section - 1/3 width on desktop */}
                <div className="lg:w-1/3">
                  {/* Chat with Specialist */}
                  <div className="mb-6">
                    <div className="flex items-center gap-6">
                      <button
                        onClick={() =>
                          openMentorModal(mentorsList.find((m) => m.id === 'eric-diepeveen')!)
                        }
                        className="size-12 cursor-pointer overflow-hidden rounded-full border-2 transition-all duration-300 hover:scale-110"
                        style={{
                          borderColor: `var(--${getBaseColor('Business & Strategy')}-300)`,
                        }}
                      >
                        <img
                          src="/assets/ericdiepeveen.webp"
                          alt="Eric Diepeveen"
                          className="size-full object-cover"
                        />
                      </button>
                      <div>
                        <h2 className="mb-1 text-base font-medium text-gray-900">
                          Need help finding the right mentor?
                        </h2>
                        <button
                          onClick={() =>
                            openMentorModal(mentorsList.find((m) => m.id === 'eric-diepeveen')!)
                          }
                          className="inline-flex items-center text-base font-light text-red-600 hover:text-red-700"
                        >
                          Send a message to Eric
                          <svg
                            className="ml-1 size-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section id="mentors-grid" className="dark:bg-gray-900">
            <div className="mx-auto max-w-screen-xl lg:px-4">
              <div className="relative dark:bg-gray-800 sm:rounded-lg">
                {/* Header with title and buttons */}
                <div className="relative z-50 pb-4 pt-3">
                  <div className="flex items-center justify-between lg:space-x-4"></div>

                  {/* Search and Filters */}
                  <div className="space-y-4 py-3">
                    {/* Search Bar */}
                    <div className="w-full md:w-72">
                      <label
                        htmlFor="default-search"
                        className="sr-only text-sm font-medium text-gray-900 dark:text-white"
                      >
                        Search
                      </label>
                      <div className="relative">
                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                          <svg
                            className="size-4 text-gray-500 dark:text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                        <input
                          type="search"
                          id="default-search"
                          className="block w-full rounded-lg border border-gray-300 bg-white p-2 pl-10 text-sm font-light text-gray-500 focus:border-primary-500 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder:text-gray-400 dark:focus:border-primary-500 dark:focus:ring-primary-500"
                          placeholder="Search mentors..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </div>

                    {/* Filters Row */}
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                      {/* Filter Buttons Container */}
                      <div
                        className="relative z-50 flex flex-wrap items-center gap-2"
                        ref={dropdownRef}
                      >
                        {/* Expertise Filter */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown('expertise');
                            }}
                            className="flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-red-700 focus:z-10 focus:outline-none focus:ring-4 focus:ring-red-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-700 dark:hover:text-white dark:focus:ring-red-700"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="mr-2 size-4 text-gray-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Expertise
                          </button>
                          <div
                            className={`absolute left-0 z-[51] mt-2 w-64 rounded-lg bg-white p-3 shadow-lg dark:bg-gray-700 ${activeDropdown === 'expertise' ? 'block' : 'hidden'}`}
                          >
                            <ul className="space-y-2 text-sm">
                              <li className="flex items-center rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-600">
                                <input
                                  id="filter-expertise-all"
                                  type="checkbox"
                                  checked={filters.expertise.includes('All')}
                                  onChange={() => handleFilterChange('expertise', 'All')}
                                  className="size-4 rounded border-gray-300 bg-gray-100 text-red-600 focus:ring-2 focus:ring-red-500 dark:border-gray-500 dark:bg-gray-600 dark:ring-offset-gray-700 dark:focus:ring-red-600"
                                />
                                <label
                                  htmlFor="filter-expertise-all"
                                  className="ml-2 text-sm font-medium text-gray-500 dark:text-gray-100"
                                >
                                  All
                                </label>
                              </li>
                              {filterOptions.expertise.map((exp) => (
                                <li
                                  key={exp}
                                  className="flex items-center rounded px-2 py-1 hover:bg-gray-50 dark:hover:bg-gray-600"
                                >
                                  <input
                                    id={`filter-expertise-${exp}`}
                                    type="checkbox"
                                    checked={filters.expertise.includes(exp)}
                                    onChange={() => handleFilterChange('expertise', exp)}
                                    className="size-4 rounded border-gray-300 bg-gray-100 text-red-600 focus:ring-2 focus:ring-red-500 dark:border-gray-500 dark:bg-gray-600 dark:ring-offset-gray-700 dark:focus:ring-red-600"
                                  />
                                  <label
                                    htmlFor={`filter-expertise-${exp}`}
                                    className={`ml-2 text-sm font-medium ${EXPERTISE_COLORS[exp]?.text || 'text-gray-500'} dark:text-gray-100`}
                                  >
                                    {exp}
                                  </label>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Stage Filter */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown('stage');
                            }}
                            className="flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-red-700 focus:z-10 focus:outline-none focus:ring-4 focus:ring-red-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-700 dark:hover:text-white dark:focus:ring-red-700"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="mr-2 size-4 text-gray-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Stage
                          </button>
                          <div
                            className={`absolute left-0 z-[51] mt-2 w-48 rounded-lg bg-white p-3 shadow-lg dark:bg-gray-700 ${activeDropdown === 'stage' ? 'block' : 'hidden'}`}
                          >
                            <ul className="space-y-2 text-sm">
                              {filterOptions.stages.map((stage) => (
                                <li key={stage} className="flex items-center">
                                  <input
                                    id={`filter-stage-${stage}`}
                                    type="checkbox"
                                    checked={filters.stages.includes(stage)}
                                    onChange={() => handleFilterChange('stages', stage)}
                                    className="size-4 rounded border-gray-300 bg-gray-100 text-red-600 focus:ring-2 focus:ring-red-500 dark:border-gray-500 dark:bg-gray-600 dark:ring-offset-gray-700 dark:focus:ring-red-600"
                                  />
                                  <label
                                    htmlFor={`filter-stage-${stage}`}
                                    className="ml-2 text-sm font-medium text-gray-500 dark:text-gray-100"
                                  >
                                    {stage}
                                  </label>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Language Filter */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown('language');
                            }}
                            className="flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-red-700 focus:z-10 focus:outline-none focus:ring-4 focus:ring-red-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-700 dark:hover:text-white dark:focus:ring-red-700"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="mr-2 size-4 text-gray-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Language
                          </button>
                          <div
                            className={`absolute left-0 z-[51] mt-2 w-48 rounded-lg bg-white p-3 shadow-lg dark:bg-gray-700 ${activeDropdown === 'language' ? 'block' : 'hidden'}`}
                          >
                            <ul className="space-y-2 text-sm">
                              {filterOptions.languages.map((lang) => (
                                <li key={lang} className="flex items-center">
                                  <input
                                    id={`filter-language-${lang}`}
                                    type="checkbox"
                                    checked={filters.languages.includes(lang)}
                                    onChange={() => handleFilterChange('languages', lang)}
                                    className="size-4 rounded border-gray-300 bg-gray-100 text-red-600 focus:ring-2 focus:ring-red-500 dark:border-gray-500 dark:bg-gray-600 dark:ring-offset-gray-700 dark:focus:ring-red-600"
                                  />
                                  <label
                                    htmlFor={`filter-language-${lang}`}
                                    className="ml-2 text-sm font-medium text-gray-500 dark:text-gray-100"
                                  >
                                    {lang} {languageToFlag(lang)}
                                  </label>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>

                        {/* Price Filter */}
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown('price');
                            }}
                            className="flex items-center justify-center rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-500 hover:bg-gray-100 hover:text-red-700 focus:z-10 focus:outline-none focus:ring-4 focus:ring-red-100 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-red-700 dark:hover:text-white dark:focus:ring-red-700"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="mr-2 size-4 text-gray-400"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Price
                          </button>
                          <div
                            className={`absolute left-0 z-[51] mt-2 w-48 rounded-lg bg-white p-3 shadow-lg dark:bg-gray-700 ${activeDropdown === 'price' ? 'block' : 'hidden'}`}
                          >
                            <ul className="space-y-2 text-sm">
                              {filterOptions.price.map((price) => (
                                <li key={price} className="flex items-center">
                                  <input
                                    id={`filter-price-${price}`}
                                    type="checkbox"
                                    checked={filters.price.includes(price)}
                                    onChange={() => handleFilterChange('price', price)}
                                    className="size-4 rounded border-gray-300 bg-gray-100 text-red-600 focus:ring-2 focus:ring-red-500 dark:border-gray-500 dark:bg-gray-600 dark:ring-offset-gray-700 dark:focus:ring-red-600"
                                  />
                                  <label
                                    htmlFor={`filter-price-${price}`}
                                    className="ml-2 text-sm font-medium text-gray-500 dark:text-gray-100"
                                  >
                                    {price}
                                  </label>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Clear Filters Button - Positioned on the right */}
                      <div className="shrink-0">
                        {hasActiveFilters ? (
                          <button
                            onClick={clearAllFilters}
                            className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                          >
                            Clear all filters
                          </button>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Loading/Error/Fallback indicators */}
              {isLoading && (
                <div className="mb-4 rounded-lg bg-blue-50 p-4 text-center text-blue-700">
                  Loading mentors...
                </div>
              )}
              {error && !isUsingFallback && (
                <div className="mb-4 rounded-lg bg-red-50 p-4 text-center text-red-700">
                  Error loading mentors: {error}
                </div>
              )}
              {isUsingFallback && (
                <div className="mb-4 rounded-lg bg-yellow-50 p-4 text-center text-sm text-yellow-700">
                  Using cached data (API unavailable)
                </div>
              )}

              {/* Card Grid Section */}
              <div className="relative z-0 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                {isLoading ? (
                  // Show skeleton loaders while loading
                  <>
                    {Array.from({ length: 6 }).map((_, index) => (
                      <MentorCardSkeleton key={index} />
                    ))}
                  </>
                ) : filteredMentors.length === 0 ? (
                  <div className="col-span-full py-10 text-center text-gray-500">
                    No mentors found matching your filters
                  </div>
                ) : (
                  filteredMentors.map((mentor) => {
                    const firstExpertiseBaseColor = mentor.expertise?.[0]
                      ? getBaseColor(mentor.expertise[0])
                      : 'gray';

                    return (
                      <div
                        key={mentor.id}
                        onClick={() => handleMentorClick(mentor.id)}
                        className={`group relative w-full cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-white text-left`}
                        style={{}}
                      >
                        {/* Banner with background image or fallback gradient */}
                        <div className="relative h-24">
                          {/* Base color background */}
                          <div
                            className="absolute inset-0"
                            style={{
                              background: `linear-gradient(to bottom, var(--${firstExpertiseBaseColor}-50) 0%, var(--${firstExpertiseBaseColor}-50) 70%, white 100%)`,
                            }}
                          />

                          {/* Overlay to improve text contrast */}
                          <div className="absolute inset-0 z-30 bg-gradient-to-b from-transparent via-transparent via-70% to-white" />

                          {/* Pattern */}
                          <div className="absolute inset-0 z-40 overflow-visible">
                            {mentorPatterns[mentor.id]}
                          </div>

                          {/* Profile picture */}
                          <div className="absolute -bottom-4 left-4 z-50">
                            <div
                              className="size-24 overflow-hidden rounded-full border-4 shadow-xl"
                              style={{
                                borderColor: `var(--${firstExpertiseBaseColor}-300, #fca5a5)`,
                              }}
                            >
                              <img
                                src={getMentorImageUrl(mentor.image)}
                                alt={mentor.name}
                                className="size-full object-cover"
                              />
                            </div>
                          </div>
                        </div>

                        {/* Profile Content */}
                        <div className="relative z-20 px-4 pb-4 pt-8">
                          <div className="space-y-4">
                            {/* Name and Info */}
                            <div>
                              <div className="flex items-center justify-between">
                                {/* Name and Languages */}
                                <div className="flex items-center gap-2">
                                  <h2 className="text-xl font-medium text-gray-500">
                                    {mentor.name}
                                  </h2>
                                  <div className="flex gap-1 text-gray-500">
                                    {mentor.languages?.map((lang) => (
                                      <span key={lang} title={lang}>
                                        {languageToFlag(lang) || lang}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Expertise as subheader */}
                              {mentor.expertise && mentor.expertise.length > 0 && (
                                <p className="mt-1.5 text-sm font-light text-gray-600">
                                  {formatExpertise(mentor.expertise)}
                                </p>
                              )}
                            </div>

                            {/* Description */}
                            {mentor.description && (
                              <div>
                                <p className="line-clamp-2 text-sm text-gray-500">
                                  {mentor.description}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          </section>
        </div>
      </main>
    </>
  );
};

export default MentorsOverviewPage;
